import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParent } from '../../contexts/parentContext';
import ProfileImage from '../../components/profileImage';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1" || "http://localhost:3000/api/v1";

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedChild } = useParent();
  
  const [event, setEvent] = useState(null);
  const [members, setMembers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${API_URL}/event/get-event-by-id?eventId=${eventId}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Event not found");
        }
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch event details");
      }

      const data = await response.json();
      console.log("Event details:", data);
      
      // Find child's attendance status
      const childId = selectedChild?._id || selectedChild?.id || selectedChild?.userId;
      const childMember = data.members?.find(member => 
        member.userId === childId || member.userId?.toString() === childId?.toString()
      );
      
      // Add child attendance status to event object
      const eventWithAttendance = {
        ...data.event,
        childAttendanceStatus: childMember?.attendanceStatus || 'pending',
        participantsCount: data.statistics?.totalMembers || data.event?.membersCount || 0,
        childMember: childMember
      };
      
      setEvent(eventWithAttendance);
      setMembers(data.members || []);
      
      // Fetch activities
      fetchActivities();

    } catch (err) {
      console.error("Error fetching event details:", err);
      setError(err.message || "Failed to load event details");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/event/get-all-activities?eventId=${eventId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (err) {
      console.error("Error fetching activities:", err);
    }
  };

  const updateChildAttendance = async (status) => {
    try {
      setAttendanceLoading(true);
      
      const token = localStorage.getItem("token");
      const childId = selectedChild?._id || selectedChild?.id || selectedChild?.userId;
      
      if (!childId) {
        throw new Error("Child ID not found");
      }

      // Map frontend status to backend status
      const statusMapping = {
        'going': 'coming',
        'not_going': 'notComing',
        'maybe': 'doNotKnow'
      };

      const backendStatus = statusMapping[status] || status;

      const response = await fetch(`${API_URL}/event/change-member-attendance-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventId,
          memberId: childId,
          status: backendStatus
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update attendance");
      }

      await fetchEventDetails();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId]);

  // Helper functions for status and type info
  const getEventStatusInfo = (event) => {
    const status = event?.eventStatus || 'Upcoming';
    
    switch (status) {
      case 'In Progress':
        return {
          status: 'Live Now',
          color: 'text-yellow-800',
          bgColor: 'bg-yellow-100',
          dotColor: 'bg-yellow-500',
          icon: '🔴'
        };
      case 'Completed':
        return {
          status: 'Completed',
          color: 'text-green-800',
          bgColor: 'bg-green-100',
          dotColor: 'bg-green-500',
          icon: '✅'
        };
      case 'Upcoming':
      default:
        return {
          status: 'Upcoming',
          color: 'text-blue-800',
          bgColor: 'bg-blue-100',
          dotColor: 'bg-blue-500',
          icon: '📅'
        };
    }
  };

  const getEventTypeInfo = (event) => {
    switch (event?.eventType) {
      case 'Match':
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      case 'Training':
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z" />
            </svg>
          ),
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        };
      default:
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
            </svg>
          ),
          color: 'text-purple-600',
          bgColor: 'bg-purple-100'
        };
    }
  };

  const getAttendanceStatusInfo = (status) => {
    if (!status || status === 'pending') {
      return {
        status: 'No Response',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100'
      };
    }

    switch (status) {
      case 'attended':
      case 'going':
      case 'coming':
        return {
          status: 'Going',
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      case 'absent':
      case 'not_going':
      case 'notComing':
        return {
          status: 'Not Going',
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      case 'maybe':
      case 'doNotKnow':
        return {
          status: 'Maybe',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100'
        };
      default:
        return {
          status: 'No Response',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <LoadingSpinner message={t("parent.loadingEventDetails")} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => navigate('/parent/child-control/events')}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t("common.back")}
            </button>
          </div>
          <ErrorMessage message={error} onRetry={fetchEventDetails} />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t("parent.eventNotFound")}
            </h2>
            <button
              onClick={() => navigate('/parent/child-control/events')}
              className="inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
            >
              {t("parent.backToEvents")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getEventStatusInfo(event);
  const typeInfo = getEventTypeInfo(event);
  const attendanceInfo = getAttendanceStatusInfo(event.childAttendanceStatus);

  // Check if this is a match with teams
  const isMatch = event.eventType === 'Match' && ((event.teamInfo && event.teamInfo.team1 && event.teamInfo.team2) || (event.team1 && event.team2));
  // Get team information for single-team events
  const singleTeam = event.team || (event.eventType !== 'Match' && event.teamInfo);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/parent/child-control/events')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t("common.back")}
          </button>
        </div>

        {/* Event Header */}
        <div className="bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden mb-8">
          {/* Hero Section */}
          <div className="relative p-8">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}
            ></div>
            
            <div className="relative z-10">
              {/* Match Header with Team Logos */}
              {isMatch ? (
                <div className="text-center mb-8">
                  {/* Teams Display */}
                  <div className="flex items-center justify-center space-x-8 mb-6">
                    {/* Team 1 */}
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-blue-100">
                        {(event.team1?.logo || event.teamInfo?.team1?.logo) ? (
                          <img
                            src={event.team1?.logo || event.teamInfo?.team1?.logo}
                            alt={event.team1?.name || event.teamInfo?.team1?.name || 'Team 1'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {event.team1?.name || event.teamInfo?.team1?.name || 'Team 1'}
                        </h3>
                        <p className="text-sm text-gray-600 capitalize">
                          {event.team1?.sport || event.teamInfo?.team1?.sport || 'Sport'} • {event.team1?.type || event.teamInfo?.team1?.type || 'Team'}
                        </p>
                      </div>
                    </div>
                    
                    {/* VS Divider */}
                    <div className="flex flex-col items-center space-y-4">
                      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full p-6 shadow-xl border-4 border-white">
                        <span className="text-2xl font-bold text-white">VS</span>
                      </div>
                      <div className="text-sm text-gray-700 text-center font-medium bg-white rounded-lg px-3 py-1 shadow-md">
                        {new Date(event.startDate).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    {/* Team 2 */}
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-green-100">
                        {(event.team2?.logo || event.teamInfo?.team2?.logo) ? (
                          <img
                            src={event.team2?.logo || event.teamInfo?.team2?.logo}
                            alt={event.team2?.name || event.teamInfo?.team2?.name || 'Team 2'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {event.team2?.name || event.teamInfo?.team2?.name || 'Team 2'}
                        </h3>
                        <p className="text-sm text-gray-600 capitalize">
                          {event.team2?.sport || event.teamInfo?.team2?.sport || 'Sport'} • {event.team2?.type || event.teamInfo?.team2?.type || 'Team'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Event Title for Matches */}
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    {event.name}
                  </h1>
                </div>
              ) : (
                /* Non-Match Event Header */
                <div className="text-center mb-8">
                  {/* Single Team Logo for Training/Events */}
                  {singleTeam && (
                    <div className="flex flex-col items-center space-y-4 mb-6">
                      <div className="w-32 h-32 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-purple-100">
                        {singleTeam.logo ? (
                          <img
                            src={singleTeam.logo}
                            alt={singleTeam.name || 'Team'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                            <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {singleTeam.name || 'Team'}
                        </h3>
                        <div className="flex items-center justify-center space-x-3">
                          <span className="text-sm px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                            {singleTeam.sport || 'Sport'}
                          </span>
                          {singleTeam.type && (
                            <span className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                              {singleTeam.type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Event Title */}
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    {event.name}
                  </h1>
                  
                  {/* Event Type and Status Badges */}
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <div className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold ${typeInfo.bgColor} ${typeInfo.color} border border-current`}>
                      {typeInfo.icon}
                      <span className="ml-2">{event.eventType}</span>
                    </div>
                    <div className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold ${statusInfo.bgColor} ${statusInfo.color} border border-current`}>
                      <div className={`w-2 h-2 rounded-full mr-2 ${statusInfo.dotColor} animate-pulse`}></div>
                      {statusInfo.status}
                    </div>
                  </div>
                </div>
              )}

              {/* Event Description */}
              {event.description && (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Description
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{event.description}</p>
                </div>
              )}

              {/* Event Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date and Time */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Schedule
                  </h3>
                  <div className="space-y-2">
                    <div className="text-gray-700">
                      <span className="font-medium">Date:</span> {new Date(event.startDate).toLocaleDateString('en-US', { 
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="text-gray-700">
                      <span className="font-medium">Time:</span> {new Date(event.startDate).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {event.endDate && (
                        <span> - {new Date(event.endDate).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location */}
                {event.metaData?.location && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Location
                    </h3>
                    <p className="text-gray-700">{event.metaData.location}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Child Attendance Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {selectedChild?.username}'s Attendance
            </h3>
            
            <div className="flex items-center justify-between mb-6">
              <div className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${attendanceInfo.bgColor} ${attendanceInfo.color}`}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {attendanceInfo.status}
              </div>
              
              {/* Show status explanation for non-upcoming events */}
              {event.eventStatus !== 'Upcoming' && (
                <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                  {event.eventStatus === 'In Progress' 
                    ? 'Event is currently in progress' 
                    : 'Event has been completed'
                  }
                </div>
              )}
            </div>

            {/* Attendance Actions - Only show for upcoming events */}
            {event.eventStatus === 'Upcoming' && (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => updateChildAttendance('going')}
                  disabled={attendanceLoading}
                  className="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {attendanceLoading ? 'Updating...' : 'Mark as Going'}
                </button>
                
                <button
                  onClick={() => updateChildAttendance('maybe')}
                  disabled={attendanceLoading}
                  className="inline-flex items-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {attendanceLoading ? 'Updating...' : 'Mark as Maybe'}
                </button>
                
                <button
                  onClick={() => updateChildAttendance('not_going')}
                  disabled={attendanceLoading}
                  className="inline-flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {attendanceLoading ? 'Updating...' : 'Mark as Not Going'}
                </button>
              </div>
            )}

            {/* Information message for completed/in-progress events */}
            {event.eventStatus !== 'Upcoming' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    {event.eventStatus === 'In Progress' 
                      ? 'Attendance cannot be changed while the event is in progress.'
                      : 'Attendance cannot be changed after the event has been completed.'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Activities */}
        {activities.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600">
              <h2 className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Event Activities
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activities.map((activity) => (
                  <div key={activity._id} className="bg-gray-50 rounded-xl p-4 hover:shadow-md transition-shadow">
                    <h3 className="font-semibold text-gray-900 mb-2">{activity.title}</h3>
                    {activity.description && (
                      <p className="text-gray-700 text-sm mb-3">{activity.description}</p>
                    )}
                    {activity.images && activity.images.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {activity.images.map((image, index) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Activity ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Event Members */}
        {members.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
              <h2 className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                Event Members ({members.length})
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map((member, index) => (
                  <div key={member.userId || member._id || index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl hover:shadow-md transition-shadow">
                    <ProfileImage 
                      image={member.user?.profilePicture || member.profileImage} 
                      alt={member.user?.fullName || member.user?.firstName || member.name}
                      user={member.user || member}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {member.user?.fullName || member.user?.firstName || member.name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500 mb-1">
                        {member.user?.accountType || member.accountType || 'Member'}
                      </p>
                      <p className={`text-sm px-2 py-1 rounded-full inline-block ${
                        member.attendanceStatus === 'coming' 
                          ? 'bg-green-100 text-green-700'
                          : member.attendanceStatus === 'notComing'
                          ? 'bg-red-100 text-red-700'
                          : member.attendanceStatus === 'doNotKnow'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {member.attendanceStatus === 'coming' 
                          ? 'Going' 
                          : member.attendanceStatus === 'notComing'
                          ? 'Not Going'
                          : member.attendanceStatus === 'doNotKnow'
                          ? 'Maybe'
                          : 'No Response'
                        }
                      </p>
                    </div>
                    {/* Team Badge */}
                    {member.teamId && event.eventType === 'Match' && (
                      <div className="text-xs">
                        {member.teamId === event.team1?._id && (
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {event.team1?.name}
                          </span>
                        )}
                        {member.teamId === event.team2?._id && (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            {event.team2?.name}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetail; 